import React, { useState, useRef } from 'react';
import { toUpper } from 'lodash';
import { SketchPicker } from 'react-color';
import { styled } from '@mui/material/styles';
import { TextField, Popover } from '@mui/material';
import { Row } from 'further-ui/components/Layout';

const MaxWidthTextField = styled(TextField)({
  maxWidth: 96,
});

const Swatch = styled('button', {
  shouldForwardProp: (prop) => prop !== 'colour',
})<{ colour?: string }>(({ colour, theme }) => ({
  height: 16,
  width: 16,
  padding: 2,
  borderRadius: '4px',
  border: `1px solid ${theme.palette.grey[300]}`,
  background: colour || 'transparent',
  cursor: 'pointer',
}));

const SwatchColour = styled('span', {
  shouldForwardProp: (prop) => prop !== 'colour',
})<{ colour?: string }>(({ colour }) => ({
  background: colour || 'transparent',
  display: 'block',
  width: '100%',
  height: '100%',
  borderRadius: '4px%',
}));

type Props = {
  value?: string;
  onChange: (value: string) => void;
};

const ColourPicker: React.FC<Props> = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const swatchRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    setAnchorEl(swatchRef.current);
  };

  const handleChange = (colour: { hex: string }) => {
    onChange(colour.hex);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Row spacing="sm" centered>
        <Swatch
          ref={swatchRef}
          onClick={handleClick}
          type="button"
          colour={value}
        >
          <SwatchColour colour={value} />
        </Swatch>
        <MaxWidthTextField
          value={toUpper(value)}
          onChange={(e) => {
            const input = e.target.value;
            if (input && !input.startsWith('#')) {
              onChange(`#${input}`);
            } else {
              onChange(input);
            }
          }}
          size="small"
        />
      </Row>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <SketchPicker color={value} onChange={handleChange} />
      </Popover>
    </>
  );
};

export default ColourPicker;
